/**
 * 水印管理器类
 * 负责全局水印的创建、更新、删除和监控
 */

import { createCanvasDataURL, ensureContainer, DEFAULT_OPTIONS } from './utils'

export default class WatermarkManager {
  /**
   * 构造函数
   * @param {Object} options 水印配置选项
   */
  constructor(options = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options }
    this.containerId = options.containerId || 'company-watermark'
    this.observer = null
    this.resizeHandler = null
    this.enabled = false
    this.container = null
  }

  /**
   * 设置水印选项
   * @param {Object} options 新的配置选项
   */
  setOptions(options = {}) {
    this.options = { ...this.options, ...options }
    if (this.enabled) {
      this.apply()
    }
  }

  /**
   * 应用水印
   */
  apply() {
    try {
      const url = createCanvasDataURL(this.options)
      this.container = ensureContainer(document.body, this.containerId, this.options.zIndex)
      this.container.style.backgroundImage = `url('${url}')`
      this._startWatching()
      this.enabled = true
    } catch (error) {
      console.error('水印应用失败:', error)
    }
  }

  /**
   * 清除水印
   */
  clear() {
    try {
      if (this.container && this.container.parentNode) {
        this.container.parentNode.removeChild(this.container)
      }
      this._stopWatching()
      this.enabled = false
      this.container = null
    } catch (error) {
      console.error('水印清除失败:', error)
    }
  }

  /**
   * 切换水印显示状态
   * @param {Boolean} flag 是否显示水印
   */
  toggle(flag) {
    if (flag === undefined) {
      flag = !this.enabled
    }
    if (flag) {
      this.apply()
    } else {
      this.clear()
    }
  }

  /**
   * 更新水印内容
   * @param {String|Array} content 新的水印内容
   */
  updateContent(content) {
    this.setOptions({ content })
  }

  /**
   * 开始监控
   * @private
   */
  _startWatching() {
    this._stopWatching()

    // 窗口大小变化时重新绘制
    if (this.options.autoResize) {
      this.resizeHandler = this._debounce(() => {
        if (this.enabled && this.container) {
          const url = createCanvasDataURL(this.options)
          this.container.style.backgroundImage = `url('${url}')`
        }
      }, 300)

      window.addEventListener('resize', this.resizeHandler)
    }

    // 防篡改监控
    if (this.options.antiTamper) {
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            // 检查水印容器是否被删除
            const watermarkExists = document.getElementById(this.containerId)
            if (!watermarkExists && this.enabled) {
              console.warn('检测到水印被删除，正在重新创建...')
              this.apply()
            }
          }
        })
      })

      this.observer.observe(document.body, {
        childList: true,
        subtree: false
      })
    }
  }

  /**
   * 停止监控
   * @private
   */
  _stopWatching() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
      this.resizeHandler = null
    }

    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  /**
   * 防抖函数
   * @param {Function} func 要防抖的函数
   * @param {Number} wait 等待时间
   * @returns {Function} 防抖后的函数
   * @private
   */
  _debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  /**
   * 销毁管理器
   */
  destroy() {
    this.clear()
  }
}
